import {
  X10_AMBIENT_MAX_TEMPERATURE,
  X10_EMPTY_KEG_LITRES,
  X10_EXPIRATION_WARNING_DAYS,
  X10_KEG_TIME_LIMIT_DAYS,
  X10_KEG_TIME_WARNING_DAYS,
  X10_VESSEL_MAX_TEMPERATURE
} from "../../constants";
import {
  IX10,
  IX10Evaluation,
  X10Headline,
  X10Level,
  X10LimitState
} from "../../store/installation/types";
import { IGroupErrors } from "../../store/outlet/types";

// Status rules for X10 machines. They follow the X10 app (src/util/helpers.ts calculateStatus) and must
// match jcore's X10ManagerStatusRules, which counts the same errors for the installation list.

const DAY_MS = 24 * 60 * 60 * 1000;

export type X10EvaluationInput = Pick<
  IX10,
  | "alive"
  | "open"
  | "leak"
  | "hardwareError"
  | "highTemperatureVessel"
  | "enhancedCoolingRunning"
  | "nfcError"
  | "vesselTemperature"
  | "ambientTemperature"
  | "lastMounted"
  | "hasKeg"
  | "volumeLeft"
  | "beverageKnown"
  | "bestBefore"
  | "hasNfcBeverage"
>;

export const daysBetween = (from: number, to: number) => Math.floor((to - from) / DAY_MS);

const limitState = (remainingDays: number, warningDays: number): X10LimitState => {
  if (remainingDays < 0) {
    return "danger";
  }
  return remainingDays < warningDays ? "warning" : "ok";
};

export function evaluateX10(x10: X10EvaluationInput, now = Date.now()): IX10Evaluation {
  const offline = !x10.alive;
  const open = !offline && x10.open;
  // an offline machine or an open tower hides every other alert, as in the X10 app
  const active = !offline && !open;

  const daysOnKeg = x10.hasKeg && x10.lastMounted ? daysBetween(x10.lastMounted, now) : undefined;
  const kegTime =
    active && daysOnKeg !== undefined
      ? limitState(X10_KEG_TIME_LIMIT_DAYS - daysOnKeg, X10_KEG_TIME_WARNING_DAYS)
      : undefined;

  // best-before only means something when the keg was identified from its NFC tag
  const daysToExpiry =
    x10.hasKeg && x10.hasNfcBeverage && x10.bestBefore
      ? daysBetween(now, x10.bestBefore)
      : undefined;
  const expiration =
    active && daysToExpiry !== undefined
      ? limitState(daysToExpiry, X10_EXPIRATION_WARNING_DAYS)
      : undefined;

  const vesselTooWarm =
    x10.vesselTemperature !== undefined && x10.vesselTemperature > X10_VESSEL_MAX_TEMPERATURE;
  const roomTooHot =
    active &&
    vesselTooWarm &&
    x10.ambientTemperature !== undefined &&
    x10.ambientTemperature > X10_AMBIENT_MAX_TEMPERATURE;
  const highTemperature = active && x10.highTemperatureVessel && !roomTooHot;
  const coolingDown = active && vesselTooWarm && !roomTooHot && !highTemperature;

  const noKeg = active && !x10.hasKeg;
  const empty = active && x10.hasKeg && x10.volumeLeft < X10_EMPTY_KEG_LITRES;
  const leak = active && x10.leak;
  const hardwareError = active && x10.hardwareError;

  const danger =
    leak || hardwareError || highTemperature || kegTime === "danger" || expiration === "danger";
  const warning =
    noKeg || empty || coolingDown || kegTime === "warning" || expiration === "warning";

  let level: X10Level = "ok";
  if (offline) {
    level = "offline";
  } else if (open || (!danger && warning)) {
    level = "warning";
  } else if (danger) {
    level = "danger";
  }

  const headlines: Array<[boolean, X10Headline]> = [
    [offline, "offline"],
    [open, "open"],
    [leak, "leak"],
    [kegTime === "danger", "keg_time"],
    [expiration === "danger", "expiration"],
    [highTemperature, "temperature"],
    [hardwareError, "hardware"],
    [noKeg, "no_keg"],
    [empty, "empty"],
    [kegTime === "warning", "keg_time"],
    [expiration === "warning", "expiration"],
    [coolingDown, "cooling_down"]
  ];
  const headline = headlines.find(([applies]) => applies)?.[1] ?? "ok";

  return {
    level,
    headline,
    offline,
    open,
    noKeg,
    empty,
    leak,
    hardwareError,
    highTemperature,
    coolingDown,
    roomTooHot,
    daysOnKeg,
    kegTime,
    daysToExpiry,
    expiration,
    beverageUnknown: x10.hasKeg && !x10.beverageKnown,
    nfcError: x10.nfcError,
    enhancedCooling: x10.enhancedCoolingRunning
  };
}

/** Error counts in the shape the installation panels and ErrorList already use. */
export function x10GroupErrors(x10s: IX10[]): IGroupErrors {
  const count = (predicate: (x10: IX10) => boolean) => x10s.filter(predicate).length;
  return {
    offline: count(x => x.evaluation.offline),
    open: count(x => x.evaluation.open),
    existsLeak: count(x => x.evaluation.leak),
    sensorError: count(x => x.evaluation.hardwareError),
    timeOnKeg: count(x => x.evaluation.kegTime === "warning" || x.evaluation.kegTime === "danger"),
    temperatureError: count(x => x.evaluation.highTemperature)
  };
}

const firmwareVersion = (value?: string) => {
  // OTA files are named x10_<major>_<minor>_<patch>_..., the "10" of the prefix is not a version
  const match = value?.replace(/^x10_/i, "").match(/(\d+)[._](\d+)[._](\d+)/);
  return match ? match.slice(1, 4).map(Number) : undefined;
};

/** Same check as the X10 app: the OTA target binary (x10_1_9_0_...) is newer than the running firmware. */
export function isFirmwareUpdateAvailable(firmware?: string, targetBinary?: string) {
  const current = firmwareVersion(firmware);
  const target = firmwareVersion(targetBinary);
  if (!current || !target) {
    return false;
  }
  for (let i = 0; i < 3; i++) {
    if (target[i] !== current[i]) {
      return target[i] > current[i];
    }
  }
  return false;
}

/** "1.9.0" out of an OTA file name such as "x10_1_9_0_release.bin". */
export function firmwareLabel(value?: string) {
  const version = firmwareVersion(value);
  return version ? version.join(".") : value;
}

/**
 * An X10 outlet whose machines were all removed (the outlet stays behind in jcore).
 * Hidden from the installation and product lists; still in the outlet picker and Performance.
 */
export const isEmptyX10Outlet = (outlet?: { isX10?: boolean; hasDevices?: boolean }) =>
  !!outlet?.isX10 && outlet.hasDevices === false;
